import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useToast } from "hooks/useToast";
import {
  useDeleteBenefitEntryMutation,
  useDeleteS174BenefitEntryMutation,
  useDeleteS174EntryMutation,
} from "store/apis/estimatedTax";
import { BackendError } from "types/utils/error";
import {
  DELETE_VARIANT_BENEFIT_ENTRY,
  DELETE_VARIANT_S174_BENEFIT_ENTRY,
  DELETE_VARIANT_S174_ENTRY,
  HTTP_STATUS_NOT_FOUND,
} from "./constants";

export type DeleteBenefitEntryVariant =
  | typeof DELETE_VARIANT_BENEFIT_ENTRY
  | typeof DELETE_VARIANT_S174_BENEFIT_ENTRY
  | typeof DELETE_VARIANT_S174_ENTRY;

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entryId: string;
  variant?: DeleteBenefitEntryVariant;
};

export const DeleteBenefitEntryModal = ({
  open,
  onClose,
  entityId,
  entryId,
  variant = DELETE_VARIANT_BENEFIT_ENTRY,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const [deleteBenefitEntry, { isLoading: isDeletingBenefitEntry }] =
    useDeleteBenefitEntryMutation();
  const [deleteS174BenefitEntry, { isLoading: isDeletingS174BenefitEntry }] =
    useDeleteS174BenefitEntryMutation();
  const [deleteS174Entry, { isLoading: isDeletingS174Entry }] =
    useDeleteS174EntryMutation();

  const isLoading =
    isDeletingBenefitEntry || isDeletingS174BenefitEntry || isDeletingS174Entry;

  const handleRemoved = () => {
    successToast({ message: "Entry removed" });
    onClose();
  };

  const handleConfirm = async () => {
    try {
      if (variant === DELETE_VARIANT_S174_BENEFIT_ENTRY) {
        await deleteS174BenefitEntry({ entityId, entryId }).unwrap();
      } else if (variant === DELETE_VARIANT_S174_ENTRY) {
        await deleteS174Entry({ entityId, entryId }).unwrap();
      } else {
        await deleteBenefitEntry({ entityId, entryId }).unwrap();
      }
      handleRemoved();
    } catch (e) {
      // The row was already deleted or replaced (stale screen, double click).
      const isAlreadyRemoved =
        variant === DELETE_VARIANT_S174_BENEFIT_ENTRY &&
        (e as BackendError)?.status === HTTP_STATUS_NOT_FOUND;

      if (isAlreadyRemoved) {
        handleRemoved();
        return;
      }

      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ?? "Failed to remove entry",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content useCustomOverlay size="small">
        <Modal.Body className="t-pt-8">
          <div className="t-mb-4 t-flex t-size-10 t-items-center t-justify-center t-rounded-full t-bg-yellow-20 t-text-orange">
            <TriangleWarning width="20" height="20" />
          </div>
          <Modal.Title className="t-mb-2 t-text-[20px]">
            Delete this entry?
          </Modal.Title>
          <p className="t-m-0 t-text-body t-text-text-30">
            This will remove the entry from your tax benefits. Your available
            balance will be updated accordingly. This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button size="regular" disabled={isLoading}>
              Cancel
            </Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            size="regular"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={handleConfirm}
          >
            Yes, delete
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
